import { ArrowRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Input } from '@/components/ui/input';
import { Section } from '../../components/blocks';
import { EmptyState } from '../../components/States';
import { useAccess } from '../../lib/access';
import { matches } from '../../lib/fold';
import { useSearchParams } from '../../lib/scopedParams';
import { SETTINGS_GROUPS, SETTINGS_ITEMS, type SettingsItem } from '../../nav/settingsIndex';

type Lookup = (key: string, o?: Record<string, unknown>) => string;

/** Translated label and description of a settings entry (`ux:settings.items.<id>`). */
export function useSettingsItemText() {
  const { t } = useTranslation('ux');
  const tr = t as unknown as Lookup;
  return (item: SettingsItem) => ({ label: tr(`settings.items.${item.id}.label`), description: tr(`settings.items.${item.id}.description`) });
}

/** The settings entries the signed-in user may open. */
export function useVisibleSettings(): readonly SettingsItem[] {
  const access = useAccess();
  return SETTINGS_ITEMS.filter((x) => !access.client && access.has(x.perms));
}

/**
 * Cài đặt › Tất cả cài đặt: every setting in one searchable list, grouped as Bán tên miền,
 * Kết nối, Tổ chức & nhân viên, Tuân thủ. Each entry opens the one place where it lives.
 */
export function SettingsIndexPage() {
  const { t } = useTranslation('ux');
  const tr = t as unknown as Lookup;
  const text = useSettingsItemText();
  const items = useVisibleSettings();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const setQ = (v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set('q', v);
    else p.delete('q');
    setParams(p, { replace: true });
  };
  const shown = items.filter((x) => {
    if (!q.trim()) return true;
    const { label, description } = text(x);
    return matches(`${label} ${description} ${x.keywords} ${tr(`settings.groups.${x.group}.label`)}`, q);
  });
  return (
    <div className="space-y-5">
      <div className="relative max-w-xl">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('settings.searchPlaceholder')}
          aria-label={t('settings.searchLabel')}
          className="h-10 bg-card pl-9"
          autoComplete="off"
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- the page is a search box first
          autoFocus
        />
      </div>
      {shown.length === 0 ? <EmptyState title={t('settings.none', { q })} /> : null}
      <div className="grid gap-5 lg:grid-cols-2">
        {SETTINGS_GROUPS.map((g) => {
          const rows = shown.filter((x) => x.group === g);
          if (!rows.length) return null;
          return (
            <Section key={g} title={tr(`settings.groups.${g}.label`)} description={tr(`settings.groups.${g}.description`)} flush>
              <ul className="divide-y border-t" data-settings-group={g}>
                {rows.map((x) => {
                  const { label, description } = text(x);
                  return (
                    <li key={x.id}>
                      <Link to={x.to} className="group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none">
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium">{label}</span>
                          <span className="block text-xs text-muted-foreground">{description}</span>
                        </span>
                        <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Section>
          );
        })}
      </div>
    </div>
  );
}
